import { useState } from 'react';
import styled from '@emotion/styled';
import { useConsumerT } from '../translations/landingConsumer';

export default function RelayPointCard({ point, selected, onClick, onSelect }) {
  const t = useConsumerT();
  const [expanded, setExpanded] = useState(false);

  if (!point) return null;

  const horaireDisplay = parseHoraire(point.horaire);

  return (
    <Card $selected={selected} onClick={() => onClick && onClick(point)}>
      <Header>
        <div>
          <Name $selected={selected}>{point.nom}</Name>
          <Address $selected={selected}>
            {point.adresse
              ? `${point.adresse}, ${point.codePostal} ${point.ville}`
              : `${point.codePostal} ${point.ville}`}
          </Address>
        </div>
        <NatureTag>{point.nature}</NatureTag>
      </Header>

      {expanded && horaireDisplay && (
        <Detail>
          <DetailLabel>{t('map.hours-title')}</DetailLabel>
          <DetailValue>{horaireDisplay}</DetailValue>
        </Detail>
      )}

      <Actions onClick={e => e.stopPropagation()}>
        {horaireDisplay && (
          <SecondaryBtn type="button" onClick={() => setExpanded(v => !v)}>
            {expanded ? t('map.action-less') : t('map.action-more')}
          </SecondaryBtn>
        )}
        {point.lat && point.lng && (
          <GoBtn
            type="button"
            onClick={() => window.open(
              `https://www.google.com/maps/dir/?api=1&destination=${point.lat},${point.lng}`,
              '_blank',
              'noopener,noreferrer'
            )}
          >
            {t('map.action-go')}
          </GoBtn>
        )}
        {onSelect && (
          <PrimaryBtn type="button" onClick={() => onSelect(point)}>
            {t('map.action-select')}
          </PrimaryBtn>
        )}
      </Actions>
    </Card>
  );
}

function parseHoraire(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return Object.entries(parsed)
      .map(([day, hours]) => `${day} : ${hours}`)
      .join('\n');
  } catch {
    return raw;
  }
}

const Card = styled.article`
  background: ${p => p.$selected ? p.theme.palette.text.primary : p.theme.palette.background.paper};
  border: 1px solid ${p => p.$selected ? p.theme.palette.text.primary : p.theme.palette.border.main};
  border-radius: 14px;
  padding: 14px 16px;
  box-shadow: ${p => p.theme.shadows[1]};
  font-family: ${p => p.theme.typography.fontFamily};
  display: flex;
  flex-direction: column;
  gap: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const Name = styled.h3`
  margin: 0 0 2px;
  font-size: 14px;
  font-weight: 700;
  color: ${p => p.$selected ? '#fff' : p.theme.palette.text.primary};
`;

const Address = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${p => p.$selected ? 'rgba(255,255,255,0.75)' : p.theme.palette.text.secondary};
  line-height: 1.4;
`;

const NatureTag = styled.span`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: ${p => p.theme.palette.text.secondary};
  padding: 4px 10px;
  border-radius: 999px;
  background: ${p => p.theme.palette.background.default};
  white-space: nowrap;
  flex-shrink: 0;
`;

const Detail = styled.div`
  padding: 10px 12px;
  background: ${p => p.theme.palette.background.default};
  border-radius: 10px;
  white-space: pre-line;
`;

const DetailLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: ${p => p.theme.palette.text.disabled};
  margin-bottom: 4px;
`;

const DetailValue = styled.div`
  font-size: 12px;
  color: ${p => p.theme.palette.text.primary};
  line-height: 1.6;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const SecondaryBtn = styled.button`
  flex: 1;
  padding: 10px 12px;
  border-radius: 999px;
  font-family: ${p => p.theme.typography.fontFamily};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: ${p => p.theme.palette.background.default};
  color: ${p => p.theme.palette.text.primary};
`;

const GoBtn = styled.button`
  flex: 1;
  padding: 10px 12px;
  border-radius: 999px;
  font-family: ${p => p.theme.typography.fontFamily};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid ${p => p.theme.palette.primary.main};
  background: transparent;
  color: ${p => p.theme.palette.primary.main};
`;

const PrimaryBtn = styled.button`
  flex: 1;
  padding: 10px 12px;
  border-radius: 999px;
  font-family: ${p => p.theme.typography.fontFamily};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: ${p => p.theme.palette.primary.main};
  color: #fff;
  box-shadow: ${p => p.theme.shadows[2]};
`;
